import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Howl } from 'howler';
import serviceAPI from '@/utils/services/serviceAPI';
import './LunarNewYearPopup.css';

// Global counter to ensure only the latest LunarNewYearPopup instance owns TTS
let __lunarTtsInstanceCounter = 0;

interface LunarNewYearPopupProps {
    idPath: string;
    onDismiss: () => void;
    /** Optional: dynamic recipient from URL params */
    greetingTitle?: string;
    greetingName?: string;
    /** Locale for language support: 'vn' | 'en' */
    locale?: string;
}

// Image assets in priority load order (critical first, heaviest last)
const IMAGE_LAYERS = [
    { key: 'bg', src: '/images/Thiep-chuc-tet/BG.jpg', priority: 0 },
    { key: 'wave-left', src: '/images/Thiep-chuc-tet/11.png', priority: 1 },
    { key: 'wave-right', src: '/images/Thiep-chuc-tet/12.png', priority: 1 },
    { key: 'tree-left', src: '/images/Thiep-chuc-tet/Mai.png', priority: 2 },
    { key: 'tree-right', src: '/images/Thiep-chuc-tet/Dao.png', priority: 2 },
    { key: 'horse-left', src: '/images/Thiep-chuc-tet/8.png', priority: 3 },
    { key: 'horse-right', src: '/images/Thiep-chuc-tet/7.png', priority: 3 },
    { key: 'logo', src: '/images/Thiep-chuc-tet/Logo GIF.gif', priority: 4 },
    { key: 'cta', src: '/images/trainghiemngay.png', priority: 5 },
];

/**
 * Preload images sequentially by priority group.
 * Each priority group loads in parallel, but groups are sequential.
 */
function preloadImagesByPriority(
    layers: typeof IMAGE_LAYERS,
    onImageLoaded: (key: string) => void,
    signal?: AbortSignal
): Promise<void> {
    const groups = new Map<number, typeof IMAGE_LAYERS>();
    layers.forEach((layer) => {
        const group = groups.get(layer.priority) || [];
        group.push(layer);
        groups.set(layer.priority, group);
    });

    const sortedPriorities = [...groups.keys()].sort((a, b) => a - b);

    return (async () => {
        for (const priority of sortedPriorities) {
            if (signal?.aborted) return;
            const group = groups.get(priority)!;
            await Promise.all(
                group.map(
                    (layer) =>
                        new Promise<void>((resolve) => {
                            if (signal?.aborted) { resolve(); return; }
                            const img = new Image();
                            img.onload = () => {
                                onImageLoaded(layer.key);
                                resolve();
                            };
                            img.onerror = () => {
                                // Still mark as loaded so animation plays even if image fails
                                onImageLoaded(layer.key);
                                resolve();
                            };
                            img.src = layer.src;
                        })
                )
            );
        }
    })();
}

export default function LunarNewYearPopup({ idPath, onDismiss, greetingTitle, greetingName, locale = 'vn' }: LunarNewYearPopupProps) {
    const [isClosing, setIsClosing] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [displayedText, setDisplayedText] = useState('');
    const [isTypingDone, setIsTypingDone] = useState(false);

    // Progressive loading state
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

    // TTS state
    const ttsHowlRef = useRef<Howl | null>(null);
    const [ttsReady, setTtsReady] = useState(false);
    const [animationsStarted, setAnimationsStarted] = useState(false);
    const ttsPlayAttemptedRef = useRef(false);
    const ttsActuallyPlayingRef = useRef(false);
    const typewriterStartedRef = useRef(false);
    const typewriterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Check if locale is English
    const isEnglish = locale?.toLowerCase() === 'en';

    // Build dynamic recipient text
    const recipientText = (greetingTitle && greetingName)
        ? `${greetingTitle} ${greetingName}`
        : (greetingTitle || greetingName || (isEnglish ? 'you' : 'bạn'));

    // Keep a ref so TTS callback always reads the latest value
    const recipientTextRef = useRef(recipientText);
    recipientTextRef.current = recipientText;

    // Keep locale ref for TTS
    const localeRef = useRef(locale);
    localeRef.current = locale;

    // Desktop: 3 lines - Vietnamese
    const desktopLine1_vn = `Kính chúc ${recipientText} một năm mới Bính Ngọ`;
    const desktopLine2_vn = 'Vạn Sự Hanh Thông – Mã Đáo Thành Công –';
    const desktopLine3_vn = 'Tài Lộc Viên Mãn – Phú Quý Dài Lâu.';

    // Desktop: 3 lines - English
    const desktopLine1_en = `Wishing ${recipientText} a prosperous Year of the Horse`;
    const desktopLine2_en = 'May All Go Well – Success in Every Endeavor –';
    const desktopLine3_en = 'Wealth and Prosperity – Happiness and Longevity.';

    // Select based on locale
    const desktopLine1 = isEnglish ? desktopLine1_en : desktopLine1_vn;
    const desktopLine2 = isEnglish ? desktopLine2_en : desktopLine2_vn;
    const desktopLine3 = isEnglish ? desktopLine3_en : desktopLine3_vn;
    const desktopFullText = `${desktopLine1}\n${desktopLine2}\n${desktopLine3}`;

    // Mobile: same 3-line format as desktop
    const mobileLine1 = desktopLine1;
    const mobileLine2 = desktopLine2;
    const mobileLine3 = desktopLine3;

    // Use ref to avoid onDismiss dependency triggering re-renders
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;

    // Track image loaded
    const handleImageLoaded = useCallback((key: string) => {
        setLoadedImages((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    }, []);

    // Block scene audio while greeting popup is showing
    const instanceIdRef = useRef(0);
    useEffect(() => {
        // Increment global counter and claim this instance
        __lunarTtsInstanceCounter++;
        instanceIdRef.current = __lunarTtsInstanceCounter;

        // Stop any leftover TTS Howl from a previous instance (e.g. F5 scenario)
        // Do NOT use Howler.unload() — it kills ALL audio including scene voiceover
        if (ttsHowlRef.current) {
            try {
                ttsHowlRef.current.stop();
                ttsHowlRef.current.unload();
            } catch (_e) { /* ignore */ }
            ttsHowlRef.current = null;
        }

        (window as any).__lunarGreetingPlaying = true;
        return () => {
            (window as any).__lunarGreetingPlaying = false;
        };
    }, []);

    // Progressive image preloading
    useEffect(() => {
        if (!isVisible) return;
        const controller = new AbortController();

        preloadImagesByPriority(IMAGE_LAYERS, handleImageLoaded, controller.signal);

        return () => { controller.abort(); };
    }, [isVisible, handleImageLoaded]);

    // Detect when core animations have started (bg + at least trees loaded)
    useEffect(() => {
        if (loadedImages.has('bg') && (loadedImages.has('tree-left') || loadedImages.has('tree-right'))) {
            // Small delay to let CSS animations begin visually
            const timer = setTimeout(() => setAnimationsStarted(true), 800);
            return () => clearTimeout(timer);
        }
    }, [loadedImages]);

    // Safety timeout: if TTS hasn't actually started playing within 15 seconds,
    // clear the blocking flag so scene audio can play
    useEffect(() => {
        const safetyTimer = setTimeout(() => {
            if (!ttsActuallyPlayingRef.current && (window as any).__lunarGreetingPlaying) {
                console.warn('[LunarTTS] Safety timeout - TTS did not start, unblocking scene audio');
                (window as any).__lunarGreetingPlaying = false;
                // Try to trigger scene audio
                if ((window as any).playCurrentSceneAudio) {
                    (window as any).playCurrentSceneAudio();
                }
            }
        }, 15000); // 15 second safety timeout
        
        return () => clearTimeout(safetyTimer);
    }, []);

    // Play TTS when both audio is ready AND animations have started (desktop auto-play)
    useEffect(() => {
        if (ttsReady && animationsStarted && !ttsPlayAttemptedRef.current && ttsHowlRef.current) {
            ttsPlayAttemptedRef.current = true;
            try {
                ttsHowlRef.current.play();
            } catch (err) {
                console.warn('[LunarTTS] Desktop autoplay failed:', err);
                // Clear flag so scene audio can play
                (window as any).__lunarGreetingPlaying = false;
            }
        }
    }, [ttsReady, animationsStarted]);

    // Mobile: if autoplay was blocked, play TTS on first user tap/click
    // This listener must be registered early (before TTS is ready) to catch user interaction
    const pendingUserInteractionRef = useRef(false);
    
    // Store ttsReady in a ref so event handler always has latest value
    const ttsReadyRef = useRef(false);
    ttsReadyRef.current = ttsReady;
    
    useEffect(() => {
        if (!isVisible) return;

        const tryPlayOnInteraction = () => {
            // Always set userHasInteracted flag on any user interaction
            (window as any).userHasInteracted = true;
            
            // If TTS is not ready yet, mark that user has interacted
            // The TTS will be played once it's ready (handled in the other useEffect)
            if (!ttsReadyRef.current || !ttsHowlRef.current) {
                pendingUserInteractionRef.current = true;
                return;
            }

            // Extra safety: if Howler says it's already playing, don't trigger again
            if (ttsHowlRef.current.playing()) return;

            // Only play if not already attempted
            if (!ttsPlayAttemptedRef.current) {
                ttsPlayAttemptedRef.current = true;
                try {
                    ttsHowlRef.current.play();
                } catch (err) {
                    console.warn('[LunarTTS] Mobile play failed:', err);
                    // If play fails, clear the blocking flag so scene audio can play
                    (window as any).__lunarGreetingPlaying = false;
                }
            }
        };

        // Register listeners early - don't use { once: true } to avoid race conditions
        // Note: Using isVisible only as dependency to avoid removing listeners when ttsReady changes
        document.addEventListener('touchstart', tryPlayOnInteraction, { passive: true });
        document.addEventListener('click', tryPlayOnInteraction);

        return () => {
            document.removeEventListener('touchstart', tryPlayOnInteraction);
            document.removeEventListener('click', tryPlayOnInteraction);
        };
    }, [isVisible]); // Only depend on isVisible, use ref for ttsReady
    
    // Handle pending user interaction - play TTS once it's ready if user already tapped
    useEffect(() => {
        if (ttsReady && ttsHowlRef.current && pendingUserInteractionRef.current && !ttsPlayAttemptedRef.current) {
            ttsPlayAttemptedRef.current = true;
            pendingUserInteractionRef.current = false;
            try {
                ttsHowlRef.current.play();
            } catch (err) {
                console.warn('[LunarTTS] Pending play failed:', err);
                (window as any).__lunarGreetingPlaying = false;
            }
        }
    }, [ttsReady]);

    // TTS: call API and preload greeting voice — ONLY ONCE per page session
    // We wait a short tick so the parent can pass down greetingTitle/greetingName
    // parsed from URL params before we fire the TTS API call.
    useEffect(() => {
        if (!isVisible) return;

        // TTS should only be called ONCE when user first enters the web page
        // If already called (this session), never call again
        if ((window as any).__lunarTtsAlreadyCalled) return;

        // Capture the instance ID at the time this effect runs
        const myInstanceId = instanceIdRef.current;
        let cancelled = false;

        // Stop and unload any previous Howl to prevent overlapping voices
        if (ttsHowlRef.current) {
            try {
                ttsHowlRef.current.stop();
                ttsHowlRef.current.unload();
            } catch (_e) { /* ignore */ }
            ttsHowlRef.current = null;
        }

        const loadTTS = async (retryCount = 0): Promise<void> => {
            const MAX_RETRIES = 3;
            const RETRY_DELAY = 1500;

            // If a newer instance has been created, abort this one
            if (myInstanceId !== __lunarTtsInstanceCounter) return;

            try {
                // Read the LATEST recipientText and locale from refs (not from the closure)
                // so that URL-parsed title/name are always included
                const currentRecipient = recipientTextRef.current;
                const currentLocale = localeRef.current;
                const isEn = currentLocale?.toLowerCase() === 'en';

                // Greeting text based on locale
                const greetingText = isEn
                    ? `Wishing ${currentRecipient} a prosperous Year of the Horse. May all go well, success in every endeavor, wealth and prosperity, happiness and longevity.`
                    : `Kính chúc ${currentRecipient} một năm mới Bính Ngọ. Vạn Sự Hanh Thông, Mã Đáo Thành Công, Tài Lộc Viên Mãn, Phú Quý Dài Lâu.`;

                // Voice based on locale
                const voiceId = isEn ? 'en-US-Wavenet-F' : 'vi-VN-Wavenet-A';

                const apiCall = async (useS3: boolean) => {
                    return await serviceAPI.textToSpeech({
                        id_path: idPath,
                        supplier: 'google',
                        text: greetingText,
                        voice: voiceId,
                        speed: 0.95,
                        format: 'mp3',
                        uploadToS3: useS3,
                    });
                };

                // Try WITHOUT S3 first (faster)
                let response;
                let audioUrl = '';

                try {
                    response = await apiCall(false);
                    if (response?.data?.audio) {
                        audioUrl = `data:audio/mp3;base64,${response.data.audio}`;
                    } else {
                        audioUrl = response?.data?.file?.url ||
                            response?.uploadInfo?.s3Url ||
                            response?.data?.audio_url ||
                            response?.audio_url ||
                            response?.url || '';
                    }
                } catch (err) {
                    // silently continue to S3 fallback
                }

                // Retry with S3 if first attempt failed
                if (!audioUrl) {
                    try {
                        response = await apiCall(true);
                        audioUrl = response?.data?.file?.url ||
                            response?.uploadInfo?.s3Url ||
                            response?.data?.audio_url ||
                            response?.audio_url ||
                            response?.url || '';
                    } catch (err) {
                        // silently continue to retry
                    }
                }

                if (!audioUrl) {
                    throw new Error('No audio URL returned');
                }

                if (cancelled || myInstanceId !== __lunarTtsInstanceCounter) return;

                // Create Howl and use onload callback to guarantee audio is buffered
                await new Promise<void>((resolve, reject) => {
                    const howl = new Howl({
                        src: [audioUrl],
                        html5: true,
                        format: ['mp3'],
                        preload: true,
                        onload: () => {
                            if (!cancelled && myInstanceId === __lunarTtsInstanceCounter) {
                                ttsHowlRef.current = howl;
                                setTtsReady(true);
                            } else {
                                // Another instance took over, discard this Howl
                                howl.unload();
                            }
                            resolve();
                        },
                        onplay: () => {
                            ttsActuallyPlayingRef.current = true;
                        },
                        onend: () => {
                            ttsActuallyPlayingRef.current = false;
                            (window as any).__lunarGreetingPlaying = false;
                            // Now trigger scene audio since greeting is done
                            setTimeout(() => {
                                if ((window as any).playCurrentSceneAudio) {
                                    (window as any).playCurrentSceneAudio();
                                }
                            }, 300);
                        },
                        onstop: () => {
                            // Called when TTS is manually stopped (e.g., user dismisses popup early)
                            ttsActuallyPlayingRef.current = false;
                            (window as any).__lunarGreetingPlaying = false;
                        },
                        onloaderror: (_id: any, err: any) => {
                            console.warn('[LunarTTS] Howl load error:', err);
                            (window as any).__lunarGreetingPlaying = false;
                            reject(new Error('Howl load error'));
                        },
                        onplayerror: (_id: any, err: any) => {
                            console.warn('[LunarTTS] Howl play error (mobile autoplay blocked?):', err);
                            // On mobile, play can fail due to autoplay policy
                            // Reset the attempted flag so the touchstart/click listener can retry
                            // Do NOT use howl.once('unlock') here - we already have document event listeners
                            // that handle replaying after user interaction. Using both causes double play.
                            ttsPlayAttemptedRef.current = false;
                            ttsActuallyPlayingRef.current = false;
                        },
                    });
                });
            } catch (err) {
                if (!cancelled && myInstanceId === __lunarTtsInstanceCounter && retryCount < MAX_RETRIES) {
                    await new Promise((r) => setTimeout(r, RETRY_DELAY));
                    if (!cancelled && myInstanceId === __lunarTtsInstanceCounter) {
                        return loadTTS(retryCount + 1);
                    }
                } else {
                    (window as any).__lunarGreetingPlaying = false;
                }
            }
        };

        // Delay TTS call slightly to let the parent parse URL params
        // (greetingTitle/greetingName) and pass them as props on next render.
        // Without this delay, recipientText is 'bạn' on the first render.
        const delayTimer = setTimeout(() => {
            if (cancelled) return;
            (window as any).__lunarTtsAlreadyCalled = true;
            loadTTS();
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(delayTimer);
            // Only unload if we NEVER attempted to play.
            // Using ttsPlayAttemptedRef (not ttsActuallyPlayingRef) because
            // play() is async — onplay hasn't fired yet when cleanup runs,
            // so ttsActuallyPlayingRef is still false = race condition.
            if (ttsHowlRef.current && !ttsPlayAttemptedRef.current) {
                try {
                    ttsHowlRef.current.stop();
                    ttsHowlRef.current.unload();
                } catch (_e) { /* ignore */ }
                ttsHowlRef.current = null;
            }
        };
    }, [isVisible, idPath]);

    // Derive a stable boolean so the effect only re-runs once when bg loads
    const bgLoaded = loadedImages.has('bg');

    // Typewriter effect - starts once bg is loaded
    useEffect(() => {
        if (!isVisible || !bgLoaded) return;
        // Prevent multiple typewriter runs
        if (typewriterStartedRef.current) return;
        typewriterStartedRef.current = true;

        const fullText = desktopFullText;
        const tokens: string[] = [];
        const lines = fullText.split('\n');
        lines.forEach((line, lineIdx) => {
            const words = line.split(' ');
            words.forEach((word, wordIdx) => {
                tokens.push(word);
                if (wordIdx < words.length - 1) tokens.push(' ');
            });
            if (lineIdx < lines.length - 1) tokens.push('\n');
        });

        // Delay typewriter start to let entrance animations finish
        const startDelay = setTimeout(() => {
            let currentIndex = 0;
            let accumulated = '';

            typewriterIntervalRef.current = setInterval(() => {
                if (currentIndex < tokens.length) {
                    const token = tokens[currentIndex];
                    if (token === ' ') {
                        accumulated += ' ';
                    } else if (token === '\n') {
                        accumulated += '\n';
                    } else {
                        accumulated += token;
                    }
                    currentIndex++;
                    setDisplayedText(accumulated);
                } else {
                    if (typewriterIntervalRef.current) {
                        clearInterval(typewriterIntervalRef.current);
                        typewriterIntervalRef.current = null;
                    }
                    setIsTypingDone(true);
                }
            }, 80);
        }, 1200);

        return () => {
            clearTimeout(startDelay);
            if (typewriterIntervalRef.current) {
                clearInterval(typewriterIntervalRef.current);
                typewriterIntervalRef.current = null;
            }
        };
    }, [isVisible, bgLoaded, desktopFullText]);

    const handleDismiss = () => {
        // Set userHasInteracted flag to enable audio autoplay for the rest of the session
        (window as any).userHasInteracted = true;

        // REMOVED: Redundant TTS play check.
        // We rely on either the initial autoplay (desktop) OR the 'touchstart/click' listener (mobile)
        // to handle the first play. 'handleDismiss' comes from a click value, allowing the global listener to pick it up if needed,
        // or if it already played, we do nothing.
        // The user specifically requested to remove the overlapping voice associated with clicking this button.

        // Do NOT stop TTS or any audio on exit.
        // Let TTS greeting finish naturally. Scene audio continues seamlessly.

        // Clear greeting playing flag so scene audio is no longer blocked
        (window as any).__lunarGreetingPlaying = false;

        setIsClosing(true);
        setTimeout(() => {
            setIsVisible(false);
            onDismiss();

            // Only trigger scene audio if TTS was NOT attempted
            // Use ttsPlayAttemptedRef (not ttsActuallyPlayingRef) because
            // ttsActuallyPlayingRef has timing delay — on mobile it may not
            // be set true yet even though play was just triggered, causing
            // scene audio to start simultaneously with TTS.
            if (!ttsPlayAttemptedRef.current) {
                setTimeout(() => {
                    if ((window as any).playCurrentSceneAudio) {
                        (window as any).playCurrentSceneAudio();
                    }
                }, 500);
            }
        }, 400);
    };

    if (!isVisible) return null;

    // Helper: check if an image layer is loaded
    const isLoaded = (key: string) => loadedImages.has(key);

    // Build displayed lines for desktop/mobile from the typing progress
    const buildDisplayLines = (lineTexts: string[]) => {
        const fullText = lineTexts.join('\n');
        const charsToShow = displayedText.length;
        const shownText = fullText.substring(0, charsToShow);
        return shownText;
    };

    const desktopDisplay = buildDisplayLines([desktopLine1, desktopLine2, desktopLine3]);
    const mobileDisplay = buildDisplayLines([mobileLine1, mobileLine2, mobileLine3]);

    return (
        <div
            className={`lunar-popup-overlay ${isClosing ? 'closing' : ''}`}
        >
            <div className="lunar-card-container">
                {/* Layer 0: Background */}
                <img
                    src="/images/Thiep-chuc-tet/BG.jpg"
                    alt="Background"
                    className={`lunar-bg ${isLoaded('bg') ? 'lunar-loaded' : ''}`}
                    draggable={false}
                    // @ts-ignore
                    fetchPriority="high"
                />

                {/* Layer 1: Trees – slide in from sides */}
                <div className={`lunar-tree-left ${isLoaded('tree-left') ? 'lunar-loaded' : ''}`}>
                    <img
                        src="/images/Thiep-chuc-tet/Mai.png"
                        alt="Cây tùng trái"
                        draggable={false}
                    />
                </div>
                <div className={`lunar-tree-right ${isLoaded('tree-right') ? 'lunar-loaded' : ''}`}>
                    <img
                        src="/images/Thiep-chuc-tet/Dao.png"
                        alt="Cây tùng phải"
                        draggable={false}
                    />
                </div>

                {/* Layer 2: Horses – gallop in from sides */}
                <div className={`lunar-horse-left ${isLoaded('horse-left') ? 'lunar-loaded' : ''}`}>
                    <img
                        src="/images/Thiep-chuc-tet/8.png"
                        alt="Ngựa trái"
                        draggable={false}
                    />
                </div>
                <div className={`lunar-horse-right ${isLoaded('horse-right') ? 'lunar-loaded' : ''}`}>
                    <img
                        src="/images/Thiep-chuc-tet/7.png"
                        alt="Ngựa phải"
                        draggable={false}
                    />
                </div>

                {/* Layer 3: Wave patterns – slide up from bottom */}
                <div className={`lunar-wave-left ${isLoaded('wave-left') ? 'lunar-loaded' : ''}`}>
                    <img
                        src="/images/Thiep-chuc-tet/11.png"
                        alt="Sóng trái"
                        draggable={false}
                    />
                </div>
                <div className={`lunar-wave-right ${isLoaded('wave-right') ? 'lunar-loaded' : ''}`}>
                    <img
                        src="/images/Thiep-chuc-tet/12.png"
                        alt="Sóng phải"
                        draggable={false}
                    />
                </div>

                {/* Layer 4: Logo GIF – above title text */}
                <div className={`lunar-logo-gif ${isLoaded('logo') ? 'lunar-loaded' : ''}`}>
                    <img
                        src="/images/Thiep-chuc-tet/Logo GIF.gif"
                        alt="Logo"
                        draggable={false}
                    />
                </div>


                {/* Layer 5a: Title top – "CHÚC MỪNG NĂM MỚI 2026" or "HAPPY NEW YEAR 2026" */}
                <div className={`lunar-title-top ${isLoaded('logo') ? 'lunar-loaded' : ''}`}>
                    <p className="lunar-title-line1">{isEnglish ? 'HAPPY NEW YEAR 2026' : 'CHÚC MỪNG NĂM MỚI 2026'}</p>
                </div>

                {/* Layer 5b: Title bottom – "Xuân Bính Ngọ" curved arc + "YEAR OF THE HORSE" */}
                <div className={`lunar-title-bottom ${isLoaded('logo') ? 'lunar-loaded' : ''}`}>
                    <svg
                        className="lunar-curved-text-svg"
                        viewBox={isEnglish ? '0 0 800 150' : '0 0 650 150'}
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <defs>
                            <path
                                id="curvedArc"
                                d={isEnglish ? 'M 10,120 Q 400,70 790,120' : 'M 30,120 Q 325,80 620,120'}
                                fill="none"
                            />
                        </defs>
                        <text textAnchor="middle">
                            <textPath href="#curvedArc" startOffset="50%">
                                {isEnglish ? 'Spring of the Horse' : 'Xuân Bính Ngọ'}
                            </textPath>
                        </text>
                    </svg>
                    <p className="lunar-title-line3">YEAR OF THE HORSE</p>
                </div>

                {/* Layer 6: Greeting Text Desktop (3 lines, nowrap per line) */}
                <div className="lunar-greeting-text lunar-greeting-desktop">
                    <span className="lunar-line">{desktopDisplay.split('\n')[0] || ''}</span>
                    {desktopDisplay.split('\n').length > 1 && (
                        <>
                            <br />
                            <span className="lunar-line">{desktopDisplay.split('\n')[1] || ''}</span>
                        </>
                    )}
                    {desktopDisplay.split('\n').length > 2 && (
                        <>
                            <br />
                            <span className="lunar-line">{desktopDisplay.split('\n')[2] || ''}</span>
                        </>
                    )}
                    {!isTypingDone && <span className="lunar-cursor">|</span>}
                </div>

                {/* Layer 6: Greeting Text Mobile (3 lines, same as desktop) */}
                <div className="lunar-greeting-text lunar-greeting-mobile">
                    <span className="lunar-line">{mobileDisplay.split('\n')[0] || ''}</span>
                    {mobileDisplay.split('\n').length > 1 && (
                        <>
                            <br />
                            <span className="lunar-line">{mobileDisplay.split('\n')[1] || ''}</span>
                        </>
                    )}
                    {mobileDisplay.split('\n').length > 2 && (
                        <>
                            <br />
                            <span className="lunar-line">{mobileDisplay.split('\n')[2] || ''}</span>
                        </>
                    )}
                    {!isTypingDone && <span className="lunar-cursor">|</span>}
                </div>

                {/* Desktop CTA button */}
                <button
                    className={`lunar-cta-button lunar-cta-desktop ${isLoaded('cta') ? 'lunar-loaded' : ''}`}
                    onClick={handleDismiss}
                    aria-label={isEnglish ? 'Experience it now' : 'Trải nghiệm ngay'}
                >
                    <img
                        src={isEnglish ? '/images/Experienceitnow.png' : '/images/trainghiemngay.png'}
                        alt={isEnglish ? 'Experience it now' : 'Trải nghiệm ngay'}
                        draggable={false}
                    />
                </button>

                {/* Mobile CTA button – using image for consistency with desktop */}
                <button
                    className={`lunar-cta-button-mobile ${isLoaded('cta') ? 'lunar-loaded' : ''}`}
                    onClick={handleDismiss}
                    aria-label={isEnglish ? 'Experience it now' : 'Trải nghiệm ngay'}
                >
                    <img
                        src={isEnglish ? '/images/Experienceitnow.png' : '/images/trainghiemngay.png'}
                        alt={isEnglish ? 'Experience it now' : 'Trải nghiệm ngay'}
                        draggable={false}
                    />
                </button>
            </div>
        </div>
    );
}
